import React, { useState, useEffect } from 'react';
import { MapPin, Calendar, Users, Clock, Info, X, ExternalLink, Search, Send, Plus, ArrowRight, Grid, List } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import Logo from '../components/Logo';
import Footer from '../components/Footer';
import WalletMenu from '../components/WalletMenu';
import { supabase } from '../lib/supabase';
import EmptyLegsSection from '../components/EmptyLegsSection';
import NavigationMenu from '../components/NavigationMenu';

export interface EmptyLegOffer {
  id: string;
  title: string;
  description: string;
  origin: string;
  destination: string;
  price: number;
  currency: string;
  departure_date: string;
  image_url: string;
  aircraft_model: string;
  aircraft_category: string;
  capacity: number;
  duration: string;
  is_featured: boolean;
  is_empty_leg: boolean;
  created_at: string;
}

const EmptyLegOffers: React.FC = () => {
  const { isAuthenticated, user } = useAuth();
  const [offers, setOffers] = useState<EmptyLegOffer[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterCategory, setFilterCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(6);
  const [totalOffers, setTotalOffers] = useState(0);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    checkAdminStatus();
    fetchTotalCount();
  }, [user]);

  useEffect(() => {
    fetchOffers();
  }, [currentPage, filterCategory, searchTerm]);

  const checkAdminStatus = async () => {
    if (!user) return;
    
    try {
      const { data: userData, error } = await supabase
        .from('users')
        .select('is_admin')
        .eq('id', user.id)
        .single();

      if (!error && userData) {
        setIsAdmin(userData.is_admin);
      }
    } catch (error) {
      console.error('Error checking admin status:', error);
    }
  };

  const fetchTotalCount = async () => {
    try {
      const { count, error } = await supabase
        .from('emptyleg_offers')
        .select('*', { count: 'exact', head: true });
      
      if (error) throw error;
      
      setTotalOffers(count || 0);
    } catch (error) {
      console.error('Error fetching total count:', error);
    }
  };

  const fetchOffers = async () => {
    try {
      setLoading(true);
      
      // Build query with filters
      let query = supabase
        .from('emptyleg_offers')
        .select('*');
      
      // Apply search filter if provided
      if (searchTerm) {
        query = query.or(
          `title.ilike.%${searchTerm}%,origin.ilike.%${searchTerm}%,destination.ilike.%${searchTerm}%,aircraft_model.ilike.%${searchTerm}%,description.ilike.%${searchTerm}%`
        );
      }
      
      // Apply category filter if not 'all'
      if (filterCategory === 'featured') {
        query = query.eq('is_featured', true);
      } else if (filterCategory === 'europe') {
        const europeanCities = ['paris', 'london', 'rome', 'barcelona', 'geneva', 'zurich', 'milan', 'vienna', 'berlin', 'amsterdam'];
        const orConditions = europeanCities.map(city => `destination.ilike.%${city}%`).join(',');
        query = query.or(orConditions);
      } else if (filterCategory === 'caribbean') {
        const caribbeanPlaces = ['bahamas', 'jamaica', 'barbados', 'st. barts', 'antigua', 'dominican', 'turks', 'caicos', 'virgin islands'];
        const orConditions = caribbeanPlaces.map(place => `destination.ilike.%${place}%`).join(',');
        query = query.or(orConditions);
      } else if (filterCategory === 'usa') {
        const usaCities = ['new york', 'miami', 'los angeles', 'las vegas', 'chicago', 'boston', 'san francisco', 'aspen', 'dallas'];
        const orConditions = usaCities.map(city => `destination.ilike.%${city}%`).join(',');
        query = query.or(orConditions);
      }
      
      // Only show future flights
      query = query.gte('departure_date', new Date().toISOString());
      
      // Order by departure date
      query = query.order('departure_date', { ascending: true });
      
      // Apply pagination
      const from = (currentPage - 1) * itemsPerPage;
      const to = from + itemsPerPage - 1;
      query = query.range(from, to);
      
      const { data, error } = await query;
      
      if (error) throw error;
      
      setOffers(data || []);
    } catch (error) {
      console.error('Error fetching offers:', error);
    } finally {
      setLoading(false);
    }
  };

  const paginate = (pageNumber: number) => {
    setCurrentPage(pageNumber);
    // Scroll to top when changing pages
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const totalPages = Math.ceil(totalOffers / itemsPerPage);

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-b from-gray-50 to-white border-b border-gray-100">
        <div className="max-w-8xl mx-auto">
          <div className="flex items-center justify-between py-4 px-6">
            <div className="flex items-center gap-8">
              <Logo />
              <NavigationMenu />
            </div>
            <div className="flex items-center gap-4">
              <WalletMenu onShowDashboard={() => {}} />
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 pt-[88px]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          {/* Hero Section */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Empty Leg Offers</h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Discover exclusive empty leg flights at significantly reduced prices.
            </p>
          </div>

          {/* Search Bar */}
          <div className="mb-8">
            <div className="max-w-md mx-auto">
              <div className="relative">
                <Search size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by destination, origin, aircraft type, or date..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1); // Reset to first page on search
                  }}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent shadow-sm"
                />
                {searchTerm && (
                  <button
                    onClick={() => {
                      setSearchTerm('');
                      setCurrentPage(1); // Reset to first page when clearing search
                    }}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X size={16} />
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap justify-center gap-2 mb-8">
            <button
              onClick={() => {
                setFilterCategory('all');
                setCurrentPage(1); // Reset to first page on filter change
              }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filterCategory === 'all' ? 'bg-black text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All Packages
            </button>
            <button
              onClick={() => {
                setFilterCategory('featured');
                setCurrentPage(1);
              }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filterCategory === 'featured' ? 'bg-black text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Featured
            </button>
            <button
              onClick={() => {
                setFilterCategory('europe');
                setCurrentPage(1);
              }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filterCategory === 'europe' ? 'bg-black text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Europe
            </button>
            <button
              onClick={() => {
                setFilterCategory('caribbean');
                setCurrentPage(1);
              }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filterCategory === 'caribbean' ? 'bg-black text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Caribbean
            </button>
            <button
              onClick={() => {
                setFilterCategory('usa');
                setCurrentPage(1);
              }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filterCategory === 'usa' ? 'bg-black text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              USA
            </button>
          </div>

          {/* Empty Leg Offers */}
          <EmptyLegsSection 
            emptyLegs={offers} 
            loading={loading} 
            onOfferClick={() => {}} 
          />

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="flex justify-center items-center mt-8">
              <button
                onClick={() => paginate(currentPage - 1)}
                disabled={currentPage === 1}
                className={`px-4 py-2 rounded-l-lg transition-colors ${
                  currentPage === 1 
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Previous
              </button>
              
              <div className="flex items-center">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  // Calculate which page numbers to show
                  let pageNum;
                  if (totalPages <= 5) {
                    // Show all pages if 5 or fewer
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    // Show first 5 pages
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    // Show last 5 pages
                    pageNum = totalPages - 4 + i;
                  } else {
                    // Show current page and 2 pages on each side
                    pageNum = currentPage - 2 + i;
                  }
                  
                  return (
                    <button
                      key={pageNum}
                      onClick={() => paginate(pageNum)}
                      className={`px-4 py-2 ${
                        currentPage === pageNum
                          ? 'bg-black text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
              
              <button
                onClick={() => paginate(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`px-4 py-2 rounded-r-lg transition-colors ${
                  currentPage === totalPages 
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Next
              </button>
            </div>
          )}

          {/* Custom Offer Banner */}
          <div className="mt-16 mb-8 bg-gradient-to-br from-blue-600 to-purple-600 rounded-2xl overflow-hidden">
            <div className="relative p-8 md:p-12">
              {/* Background Pattern */}
              <div className="absolute inset-0 opacity-10">
                <div className="absolute inset-0" style={{
                  backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg' %3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                  backgroundSize: '30px 30px'
                }}></div>
              </div>
              
              {/* Content */}
              <div className="relative flex flex-col md:flex-row items-center justify-between gap-8">
                <div className="text-white max-w-xl">
                  <h2 className="text-2xl md:text-3xl font-bold mb-4">
                    Looking for Something More Specific?
                  </h2>
                  <p className="text-white/90 text-lg mb-6">
                    Our expert team can create a custom charter solution tailored to your exact requirements. From unique routes to special amenities, we'll craft the perfect journey for you.
                  </p>
                  <div className="flex flex-wrap gap-4">
                    <a 
                      href="/contact" 
                      className="inline-flex items-center gap-2 bg-white text-black px-6 py-3 rounded-xl font-medium hover:bg-opacity-90 transition-colors"
                    >
                      <Send size={18} />
                      <span>Request Custom Quote</span>
                    </a>
                  </div>
                </div>
                
                <div className="hidden md:block">
                  <div className="w-64 h-64 relative">
                    <div className="absolute inset-0 bg-white/10 backdrop-blur-sm rounded-2xl transform rotate-6"></div>
                    <div className="absolute inset-0 bg-white/10 backdrop-blur-sm rounded-2xl transform -rotate-3"></div>
                    <div className="absolute inset-0 bg-white rounded-2xl shadow-lg flex items-center justify-center p-6">
                      <img 
                        src="https://raw.githubusercontent.com/stackblitz/private-jet-icons/main/jets/challenger-350.webp" 
                        alt="Custom Charter" 
                        className="w-full h-full object-contain"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <Footer />
    </div>
  );
}

export default EmptyLegOffers;